import {
    createContext,
    useContext,
    useId,
    type ComponentPropsWithRef,
    type ReactNode,
} from 'react';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/registry';
import { cn } from '../lib/cn';
import { disabledStyles, focusRing } from '../lib/styles';

/**
 * Control sizes, measured from the prototype:
 * - `sm` 42px · 13px text · padding 10/12 · radius 7 — filter bar, search, sort
 * - `md` 44px · 14px text · padding 11/12 · radius 7 — every in-app form (opname, EP)
 * - `lg` 46px · 15px text · padding 13/14 · radius 8 — login, public share field
 *
 * `md` is the floor inside opname and on the public page (44px touch target).
 */
export type FieldSize = 'sm' | 'md' | 'lg';

const controlSize: Record<FieldSize, string> = {
    sm: 'min-h-[42px] px-3 py-[10px] text-[13px] rounded-[7px]',
    md: 'min-h-[44px] px-3 py-[11px] text-[14px] rounded-[7px]',
    lg: 'min-h-[46px] px-[14px] py-[13px] text-[15px] rounded-[8px]',
};

/** Shared shell: white, 1px `--border-control`, explicit text colour. */
const controlShell = cn(
    'block w-full font-[family-name:var(--font-body)] leading-[1.35]',
    'bg-[var(--surface-card)] text-[var(--text-primary)]',
    'border border-[var(--border-control)]',
    'placeholder:text-[var(--text-tertiary-safe)]',
    'hover:border-[var(--accent)]',
    'aria-[invalid=true]:border-[var(--danger)]',
    focusRing,
    disabledStyles,
    'disabled:bg-[var(--surface-subtle)]',
);

type FieldContextValue = {
    controlId: string;
    labelId: string;
    descriptionId?: string;
    errorId?: string;
    invalid: boolean;
    size: FieldSize;
};

const FieldContext = createContext<FieldContextValue | null>(null);

/**
 * The wiring a control inside a `Field` needs: its id, the label and help ids,
 * the invalid state and the size. Outside a `Field` everything is empty and the
 * size falls back to `md`. `Dropdown` uses this too.
 */
export function useFieldControl(size?: FieldSize) {
    const ctx = useContext(FieldContext);
    return {
        inField: ctx !== null,
        id: ctx?.controlId,
        labelId: ctx?.labelId,
        describedBy: [ctx?.descriptionId, ctx?.errorId].filter(Boolean).join(' ') || undefined,
        invalid: ctx?.invalid ?? false,
        size: size ?? ctx?.size ?? 'md',
    };
}

const useField = useFieldControl;

export type FieldProps = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
    /** 12px `--text-secondary`. Labels are nouns: "Herstelkosten", not "Vul kosten in". */
    label: ReactNode;
    /** Login uses shouting caps ("E-MAIL"); in-app forms use sentence case. */
    labelCase?: 'sentence' | 'caps';
    description?: ReactNode;
    /** Present means invalid: the control gets `aria-invalid` and a red border. */
    error?: ReactNode;
    /** Size flows down to the control inside. */
    size?: FieldSize;
    children: ReactNode;
};

/**
 * Label + control + help/error, wired with `htmlFor` / `aria-describedby`.
 * Wrap exactly one `Input`, `Select` or `Textarea`.
 */
export function Field({
    label,
    labelCase = 'sentence',
    description,
    error,
    size = 'md',
    className,
    children,
    ...rest
}: FieldProps) {
    const base = useId();
    const controlId = `${base}-control`;
    const labelId = `${base}-label`;
    const descriptionId = description ? `${base}-description` : undefined;
    const errorId = error ? `${base}-error` : undefined;

    return (
        <FieldContext.Provider
            value={{ controlId, labelId, descriptionId, errorId, invalid: Boolean(error), size }}
        >
            <div className={cn('flex flex-col gap-[5px]', className)} {...rest}>
                <label
                    id={labelId}
                    htmlFor={controlId}
                    className={cn(
                        'font-[family-name:var(--font-body)] text-[12px] leading-[1.3] text-[var(--text-secondary)]',
                        labelCase === 'caps' && 'font-medium tracking-[0.02em] uppercase',
                    )}
                >
                    {label}
                </label>
                {children}
                {description ? (
                    <span
                        id={descriptionId}
                        className="font-[family-name:var(--font-body)] text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]"
                    >
                        {description}
                    </span>
                ) : null}
                {error ? (
                    <span
                        id={errorId}
                        className="font-[family-name:var(--font-body)] text-[12px] leading-[1.4] text-[var(--danger)]"
                    >
                        {error}
                    </span>
                ) : null}
            </div>
        </FieldContext.Provider>
    );
}

export type InputProps = Omit<ComponentPropsWithRef<'input'>, 'size'> & {
    size?: FieldSize;
    /** Amounts, codes and times are monospaced (principle 9). */
    mono?: boolean;
    /**
     * A line icon at the start of the field — `search` for a search field. With an
     * icon the field gets a wrapper, and `className` goes on that wrapper: pass
     * widths there (`w-[228px]`, `flex-1`).
     */
    icon?: IconName;
};

export function Input({
    size,
    mono = false,
    icon,
    className,
    id,
    'aria-describedby': describedBy,
    'aria-invalid': invalid,
    ...rest
}: InputProps) {
    const field = useField(size);
    const input = (
        <input
            id={id ?? field.id}
            aria-invalid={invalid ?? (field.invalid || undefined)}
            aria-describedby={describedBy ?? field.describedBy}
            className={cn(
                controlShell,
                controlSize[field.size],
                mono && 'font-[family-name:var(--font-mono)]',
                icon ? 'peer pl-9' : className,
            )}
            {...rest}
        />
    );

    if (!icon) {
        return input;
    }

    return (
        <span className={cn('relative block w-full', className)}>
            {input}
            <Icon
                name={icon}
                size="sm"
                tone="muted"
                className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 peer-disabled:opacity-50"
            />
        </span>
    );
}

export type SelectProps = Omit<ComponentPropsWithRef<'select'>, 'size'> & {
    size?: FieldSize;
};

/** Room on the right for the chevron, per size. */
const selectChevronSpace: Record<FieldSize, string> = {
    sm: 'pr-9',
    md: 'pr-9',
    lg: 'pr-10',
};

/**
 * Never free text where a list will do (principle 1). The native select keeps the
 * platform picker on tablets, which is faster than any custom listbox on site —
 * so this stays a real `<select>` in forms and in the opname. It only drops the
 * browser's own arrow for the design-system chevron, so it is the same height and
 * shape as an `Input` in every browser.
 *
 * For a filter or sort control in an office toolbar, use `Dropdown`.
 *
 * `className` goes on the wrapper: pass widths there (`w-auto`, `min-w-[136px]`).
 */
export function Select({
    size,
    className,
    children,
    id,
    'aria-describedby': describedBy,
    'aria-invalid': invalid,
    ...rest
}: SelectProps) {
    const field = useField(size);

    return (
        <span className={cn('relative block w-full', className)}>
            <select
                id={id ?? field.id}
                aria-invalid={invalid ?? (field.invalid || undefined)}
                aria-describedby={describedBy ?? field.describedBy}
                className={cn(
                    controlShell,
                    controlSize[field.size],
                    selectChevronSpace[field.size],
                    'peer cursor-pointer appearance-none truncate',
                )}
                {...rest}
            >
                {children}
            </select>
            <Icon
                name="chevronDown"
                size="sm"
                tone="muted"
                className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 peer-disabled:opacity-50"
            />
        </span>
    );
}

export type TextareaProps = ComponentPropsWithRef<'textarea'> & {
    size?: FieldSize;
};

/**
 * The one place free text is allowed: the finding text for the report. Minimum
 * height 64px, matching the note field in the opname detail block. Pair it with
 * the voice button — dictation is the primary input on site.
 */
export function Textarea({
    size,
    className,
    id,
    'aria-describedby': describedBy,
    'aria-invalid': invalid,
    ...rest
}: TextareaProps) {
    const field = useField(size);

    return (
        <textarea
            id={id ?? field.id}
            aria-invalid={invalid ?? (field.invalid || undefined)}
            aria-describedby={describedBy ?? field.describedBy}
            className={cn(
                controlShell,
                controlSize[field.size],
                'min-h-16 resize-y leading-[1.5]',
                className,
            )}
            {...rest}
        />
    );
}
